import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Input, Select, Checkbox, message } from 'antd';
import { ChevronLeftIcon, EditSquareIcon } from '../../../assets/icons';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { BIND_CHIP, BIND_LABEL, isOwn, type Binding } from './mockData';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetStateDepartmentQuery,
  useGetTerritoriesQuery,
  useGetPeopleQuery,
  useGetUnitsQuery,
  useGetLevelNodesQuery,
  useUpdateStateDepartmentMutation,
  useSyncLevelResourcesMutation,
  useGetAnchorLevelsQuery,
  useGetBindingsQuery,
  useGetValidationQuery,
  useGetVillageLookupQuery,
  useGetDepartmentGovernmentQuery,
  type AnchorLevel,
  type BindingOption,
  type StateDeptLevel,
  type StateDepartmentDetail,
  type Unit,
} from '../../../features/platform/platformApi';
import { useGetUsersQuery, userName } from '../../../features/users/usersApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — department detail (prototype `isDeptView`), routed by
 * code: /admin/departments/:code. Four tabs: Structure, Territories,
 * People, Minister & committees. Wired to the Platform Admin API.
 * ------------------------------------------------------------------ */

const covColor = (c: number) => (c >= 100 ? 'text-brand' : c >= 70 ? 'text-[#8a4b0f]' : 'text-[#b42318]');
const barColor = (c: number) => (c >= 100 ? 'bg-brand' : c >= 70 ? 'bg-[#8a4b0f]' : 'bg-[#b42318]');

/** Village-lookup dot per binding: solid (anchor), dashed ring (inherited), orange ring (own). */
const dotClass = (b: Binding) =>
  b === 'anchor'
    ? 'bg-brand'
    : b === 'inherited'
      ? 'border-[1.5px] border-dashed border-brand bg-white'
      : isOwn(b)
        ? 'border-[1.5px] border-[#c2410c] bg-[#fdf4ec]'
        : 'border border-[#d1d5db] bg-white';

const sourceText = (l: {
  binding: Binding;
  name: string;
  source?: string | null;
  source_level?: string | null;
}) => {
  // Use the level's actual saved source/source_level (e.g. "ECI › District"), not a hardcoded one.
  const label = l.source ? `${l.source} › ${l.source_level || l.name}` : null;
  if (l.binding === 'anchor') return label || 'anchored';
  if (l.binding === 'inherited') return label || 'inherited from the anchor above';
  if (l.binding === 'facility' || l.binding === 'none') return 'listed one by one';
  return 'members not chosen yet';
};

const TABS = ['Structure', 'Territories', 'People', 'Minister & committees'] as const;
type Tab = (typeof TABS)[number];

const BINDING_OPTS: { value: Binding; label: string }[] = [
  { value: 'anchor', label: 'Anchor' },
  { value: 'inherited', label: 'Inherited' },
  { value: 'grouped', label: 'Own (made of a level)' },
  { value: 'facility', label: 'Own · listed' },
];

/** Local starters for the special state-only hierarchies, used when the API
 *  has no record yet (e.g. opening the ad-hoc builder before it is saved). */
const makeFallback = (code: string, stateName: string): StateDepartmentDetail | null => {
  const lv = (rows: [string, Binding, string?][]): StateDeptLevel[] =>
    rows.map(([name, binding, official_post], i) => ({
      position: i + 1,
      name,
      binding,
      binding_label: BIND_LABEL[binding],
      official_post: official_post ?? '',
      elected_post: '',
    }));
  if (code === 'adhoc' || code === 'AH') {
    return {
      id: 0, code: 'adhoc', name: `${stateName}-only department`, control: '—', bottom_unit: 'Area',
      customised: true, reviewed: false, coverage: null, minister: null, level_count: 4,
      levels_source: 'own',
      levels: lv([['State', 'anchor'], ['District', 'anchor'], ['New own level', 'grouped'], ['Village', 'inherited']]),
    };
  }
  if (code === 'party') {
    return {
      id: 0, code: 'party', name: 'Party organisation', control: 'Party', bottom_unit: 'Booth',
      customised: true, reviewed: false, coverage: null, minister: null, level_count: 6,
      levels_source: 'own',
      levels: lv([['State', 'anchor'], ['Zila', 'grouped'], ['Assembly', 'anchor'], ['Mandal', 'grouped'], ['Shakti Kendra', 'booth'], ['Booth', 'inherited']]),
    };
  }
  return null;
};

/** People tab — the per-unit record panel for one expanded ladder row. */
function LevelPeople({
  stateCode,
  stateName,
  deptCode,
  deptName,
  level,
  designation,
  type,
}: {
  stateCode: string;
  stateName: string;
  deptCode: string;
  deptName: string;
  level: string;
  designation: string;
  type: 'official' | 'elected';
}) {
  const navigate = useNavigate();
  const guard = !stateCode || !deptCode || !level || !designation;
  // Units of THIS level come from level-nodes: Own levels -> their own units,
  // Anchored/Inherited -> the real source nodes (District -> districts, Tehsil
  // (ECI Booth) -> booths). This makes the per-unit list match the "of N" count.
  const { data: nodesResp } = useGetLevelNodesQuery(
    { code: stateCode, template_code: deptCode, level },
    { skip: guard },
  );
  // Who holds this post is read from the people directory (elected vs bureaucracy),
  // scoped to this designation and matched to each unit by location name.
  const { data: dirUsers } = useGetUsersQuery(
    { directory: type === 'elected' ? 'elected' : 'bureaucracy', designation, all: true },
    { skip: guard },
  );
  const byLocation = useMemo(() => {
    const m = new Map<string, { id: number; name: string }>();
    (dirUsers?.items ?? []).forEach((u) => {
      const loc = u.location_name;
      const nm = userName(u);
      if (loc && nm && nm !== '—' && !m.has(loc)) m.set(loc, { id: u.id, name: nm });
    });
    return m;
  }, [dirUsers]);

  const units = useMemo(
    () => (nodesResp?.nodes ?? []).map((n) => ({ id: n.node_code, name: n.node_name })),
    [nodesResp],
  );

  // Returning from the add/edit form reopens this department's People tab with the same level.
  const back = `/admin/departments/${deptCode}?tab=People&level=${encodeURIComponent(level)}&designation=${encodeURIComponent(designation)}`;

  // Clicking "Gap" opens the add form prefilled with department, designation, level and unit.
  const openAddForm = (unitName: string) => {
    const dir = type === 'elected' ? 'political' : 'government-official';
    const q =
      `?designation=${encodeURIComponent(designation)}` +
      `&level=${encodeURIComponent(level)}` +
      `&location=${encodeURIComponent(unitName)}` +
      `&returnTo=${encodeURIComponent(back)}` +
      (dir === 'government-official' ? `&dept=${encodeURIComponent(deptName)}` : '');
    navigate(`/admin/departments/${deptCode}/people/${dir}/new${q}`);
  };

  // Editing a recorded person opens the directory edit form (by user id), returning here.
  const openEditForm = (id: number) =>
    navigate(`/admin/users/${id}/edit?returnTo=${encodeURIComponent(back)}`);

  return (
    <section className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
      <h2 className="text-base font-bold text-ink">
        {designation} · {level}
      </h2>
      <span className="text-[13px] text-ink-secondary">
        Who holds the post is read from the people directory. Click <b>Gap</b> to add a missing person;
        once added they appear here and are shared with every {stateName} client straight away.
      </span>
      <div className="mt-1 flex flex-col">
        {units.length === 0 && <span className="py-2 text-[13px] text-ink-secondary">No units mapped for this level yet.</span>}
        {units.map((u) => {
          const rec = byLocation.get(u.name);
          return (
            <div
              key={u.id}
              className="grid grid-cols-[minmax(120px,0.8fr)_minmax(200px,1.4fr)_minmax(140px,1fr)_minmax(110px,auto)] items-center gap-3 border-t border-[#f3f4f6] py-3 first:border-t-0"
            >
              <span className="text-sm font-bold text-ink">{u.name}</span>
              <Input value={rec?.name ?? ''} readOnly placeholder="Not found" />
              <span className="text-[13px] text-ink-secondary">{rec ? 'From people directory' : 'Not found in public sources'}</span>
              <span className="flex items-center justify-end gap-1.5">
                {rec ? (
                  <>
                    <span className="rounded bg-brand-shade px-2 py-0.5 text-[11px] font-semibold text-brand">
                      Recorded
                    </span>
                    <button
                      type="button"
                      onClick={() => openEditForm(rec.id)}
                      title="Edit this person"
                      className="flex size-7 items-center justify-center rounded-lg border border-line bg-white text-ink-secondary transition-colors hover:border-brand hover:text-brand"
                    >
                      <EditSquareIcon className="size-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => openAddForm(u.name)}
                    title="Add this person in the directory"
                    className="rounded bg-[#fde3e3] px-2 py-0.5 text-[11px] font-semibold text-[#991b1b] transition-colors hover:bg-[#fbcaca]"
                  >
                    Gap
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface StructRow {
  id: string;
  name: string;
  binding: Binding;
  post: string;
  epost: string;
  /** Source system (LGD | ECI | SEC | user) an Anchored level references — saved back as-is. */
  source?: string;
  /** The specific source level this level mirrors (e.g. "Parliament") — round-trips via the API. */
  sourceLevel?: string;
  /** In-session display label for the chosen anchor source (e.g. "ECI › State"). */
  sourceLabel?: string;
  /** Position in the original template/state levels this row came from; -1 if newly added. */
  orig: number;
}

const fromLevels = (levels: StateDeptLevel[]): StructRow[] =>
  levels.map((l, i) => ({
    id: `r${i}`,
    name: l.name,
    binding: l.binding,
    post: l.official_post ?? '',
    epost: l.elected_post ?? '',
    source: l.source,
    sourceLevel: l.source_level,
    orig: i,
  }));

/** Minimal shape of a level row for anchor/inherited source resolution. */
interface SrcRow {
  binding: Binding;
  name: string;
  source?: string;
  /** In-session display label of the chosen source (e.g. "ECI › State"), when picked. */
  sourceLabel?: string;
}

/** The nearest Anchored level above `idx` — an Inherited level mirrors its source. */
const nearestAnchorAbove = <T extends { binding: Binding }>(rows: T[], idx: number): T | undefined => {
  for (let j = idx - 1; j >= 0; j -= 1) if (rows[j].binding === 'anchor') return rows[j];
  return undefined;
};

/** The static hierarchy source system an Anchored row references (its pick, else matched by name). */
const anchorSourceSystem = (ar: SrcRow, anchorLevels: AnchorLevel[]): string | undefined => {
  if (ar.sourceLabel) {
    const byLabel = anchorLevels.find((a) => a.label === ar.sourceLabel);
    if (byLabel) return byLabel.source;
  }
  return ar.source ?? anchorLevels.find((a) => a.level.toLowerCase() === ar.name.toLowerCase())?.source;
};

/** The specific source LEVEL an Anchored row is anchored at. */
const anchorSourceLevel = (ar: SrcRow, anchorLevels: AnchorLevel[]): string | undefined => {
  if (ar.sourceLabel) {
    const byLabel = anchorLevels.find((a) => a.label === ar.sourceLabel);
    if (byLabel) return byLabel.level;
  }
  const sys = anchorSourceSystem(ar, anchorLevels);
  return anchorLevels.find((a) => a.source === sys && a.level.toLowerCase() === ar.name.toLowerCase())?.level;
};

/**
 * Nodes BELOW the anchor's node in the SAME hierarchy — the only sources an Inherited level may
 * mirror (same static hierarchy, child levels of the anchor). The source system can't be switched.
 */
/**
 * The source levels an Inherited row may pick — only levels DEEPER than the deepest source level
 * already used by an Anchor OR Inherited row ABOVE it, within that same source. anchor-levels is
 * ordered by depth (ECI: State, Parliament, Assembly, District, Booth), so "deeper" = higher index.
 * e.g. District (index 3) anchored above → the next inherit can only be Booth (index 4).
 *
 * The anchor-levels array order IS the depth order — it is never sorted; we render it as-is.
 */
const inheritOptionsFor = (
  rows: (SrcRow & { sourceLevel?: string })[],
  idx: number,
  anchorLevels: AnchorLevel[],
): { options: AnchorLevel[]; system?: string } => {
  let usedIndex = -1;
  let usedSource: string | undefined;
  for (let j = 0; j < idx; j += 1) {
    const row = rows[j];
    if (row.binding !== 'anchor' && row.binding !== 'inherited') continue;
    // The source + level this row occupies (anchors resolve their mapped level; inherited rows
    // carry the mirrored source + source_level).
    const sys = row.binding === 'anchor' ? anchorSourceSystem(row, anchorLevels) : row.source;
    const lvl = row.binding === 'anchor' ? anchorSourceLevel(row, anchorLevels) : row.sourceLevel;
    if (!sys || !lvl) continue;
    const k = anchorLevels.findIndex((a) => a.source === sys && a.level === lvl);
    if (k > usedIndex) {
      usedIndex = k;
      usedSource = sys;
    }
  }
  if (!usedSource) return { options: [] };
  return { options: anchorLevels.filter((a, i) => a.source === usedSource && i > usedIndex), system: usedSource };
};

/** The effective Source/members for a row — the single source of truth for both the cell and save. */
interface EffectiveSource {
  system?: string;
  level?: string;
  label?: string;
  options: AnchorLevel[];
  anchorName?: string;
}
const effectiveSource = (
  rows: (SrcRow & { sourceLevel?: string })[],
  idx: number,
  anchorLevels: AnchorLevel[],
): EffectiveSource => {
  const r = rows[idx];
  // Prefer the in-session pick (sourceLabel), then a saved source+level, then a name match.
  const labelFrom = (opts: AnchorLevel[]): string | undefined =>
    (r.sourceLabel && opts.some((a) => a.label === r.sourceLabel) ? r.sourceLabel : undefined) ??
    (r.source && r.sourceLevel
      ? opts.find((a) => a.source === r.source && a.level === r.sourceLevel)?.label
      : undefined) ??
    opts.find((a) => a.level.toLowerCase() === r.name.toLowerCase())?.label;

  if (r.binding === 'anchor') {
    const label = labelFrom(anchorLevels);
    const picked = anchorLevels.find((a) => a.label === label);
    return {
      system: picked?.source ?? r.source,
      level: picked?.level ?? r.sourceLevel,
      label,
      options: anchorLevels,
    };
  }
  if (r.binding === 'inherited') {
    // Only levels deeper than the deepest source level used above this row (same source).
    const { options, system } = inheritOptionsFor(rows, idx, anchorLevels);
    const label = labelFrom(options);
    const picked = options.find((a) => a.label === label);
    return {
      system,
      level: picked?.level ?? r.sourceLevel,
      label,
      options,
      anchorName: nearestAnchorAbove(rows, idx)?.name,
    };
  }
  return { options: [] };
};

/** Structure tab — the state's levels, editable, saved back as overrides. */
function StructureTab({
  dept,
  stateCode,
  stateName,
}: {
  dept: StateDepartmentDetail;
  stateCode: string;
  stateName: string;
}) {
  // Source levels (imported LGD/ECI/SEC) an Anchored level can point at — for the per-row picker.
  const { data: anchorLevels = [] } = useGetAnchorLevelsQuery(stateCode, { skip: !stateCode });

  // Binding types + their help text (single source of truth — GET /platform/bindings/), falling
  // back to the local labels only until the list loads.
  const { data: bindingDefs = [] } = useGetBindingsQuery();
  const bindingOptions: BindingOption[] = bindingDefs.length
    ? bindingDefs
    : BINDING_OPTS.map((o) => ({ value: o.value, label: o.label }));
  const bindingDesc = useMemo(
    () => Object.fromEntries(bindingDefs.map((b) => [b.value, b.description] as const)),
    [bindingDefs],
  );

  // Capture the ORIGINAL levels once, so per-row change tags compare against them.
  const original = useMemo(() => dept.levels, [dept]);
  const [rows, setRows] = useState<StructRow[]>(() => fromLevels(dept.levels));
  // Re-sync rows whenever the server detail changes — after a save/reset the department-detail
  // query refetches (RTK invalidation), and the Source column must reflect the persisted
  // source / source_level rather than the stale in-session rows. RTK's structural sharing keeps
  // `dept.levels` referentially stable while editing, so this only fires on real data changes.
  useEffect(() => {
    setRows(fromLevels(dept.levels));
  }, [dept.levels]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [save] = useUpdateStateDepartmentMutation();
  const [resetDept] = useUpdateStateDepartmentMutation();

  // Per-row change tags: compare each editable row to its original level.
  const TAG = 'rounded px-1.5 py-0.5 text-[11px] font-semibold';
  const tagsFor = (r: StructRow): { label: string; cls: string }[] => {
    const o = r.orig >= 0 ? original[r.orig] : undefined;
    const tags: { label: string; cls: string }[] = [];
    if (!o) {
      tags.push({ label: `Added for ${stateName}`, cls: 'bg-[#fbeadb] text-[#73300b]' });
      return tags;
    }
    if (r.name !== o.name) tags.push({ label: 'Renamed', cls: 'bg-brand-shade text-brand' });
    if (r.post !== (o.official_post ?? '')) tags.push({ label: 'Post renamed', cls: 'bg-brand-shade text-brand' });
    if (r.epost !== (o.elected_post ?? '')) tags.push({ label: 'Elected post renamed', cls: 'bg-[#ede7f6] text-[#4a2c82]' });
    if (r.binding !== o.binding) tags.push({ label: 'Binding changed', cls: 'bg-brand-shade text-brand' });
    return tags;
  };

  const onReset = async () => {
    setResetting(true);
    setSaved(false);
    try {
      await resetDept({ code: stateCode, tcode: dept.code, patch: { reset: true } }).unwrap();
      setRows(fromLevels(original)); // optimistic reset to original levels
    } catch {
      /* surfaced elsewhere */
    } finally {
      setResetting(false);
    }
  };

  const upd = (id: string, patch: Partial<StructRow>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  // Change a row's binding, then clear any row left as an orphaned "Inherited" (no Anchor above
  // it anymore) — drop its binding + source so the user re-chooses instead of a stale value.
  const changeBinding = (id: string, binding: Binding) =>
    setRows((rs) => {
      const next = rs.map((r) => (r.id === id ? { ...r, binding } : r));
      return next.map((r, i) =>
        r.binding === 'inherited' && !nearestAnchorAbove(next, i)
          ? { ...r, binding: '' as Binding, sourceLabel: undefined, source: undefined, sourceLevel: undefined }
          : r,
      );
    });
  const drop = (id: string) => setRows((rs) => rs.filter((r) => r.id !== id));
  const insertBelow = (id: string) =>
    setRows((rs) => {
      const idx = rs.findIndex((r) => r.id === id);
      const nr: StructRow = { id: `n${Date.now()}`, name: 'New own level', binding: 'grouped', post: '', epost: '', orig: -1 };
      return [...rs.slice(0, idx + 1), nr, ...rs.slice(idx + 1)];
    });

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await save({
        code: stateCode,
        tcode: dept.code,
        patch: {
          levels: rows.map((r, i) => {
            // Persist the EFFECTIVE Source/members shown in the row (not just manual picks):
            //  - anchor: the system + chosen/defaulted source level
            //  - inherited: the nearest Anchored level's system (locked) + mirrored child node
            //  - own: none (members come from the units editor)
            const eff = effectiveSource(rows, i, anchorLevels);
            return {
              name: r.name,
              binding: r.binding,
              official_post: r.post || undefined,
              elected_post: r.epost || undefined,
              // Persist source system + specific level (previously omitted → wiped every save).
              source: eff.system || undefined,
              source_level: eff.level || undefined,
            };
          }),
        },
      }).unwrap();
      setSaved(true);
    } catch {
      /* surfaced elsewhere */
    } finally {
      setSaving(false);
    }
  };

  const GRID = 'grid grid-cols-[minmax(130px,1fr)_minmax(150px,1.1fr)_minmax(150px,1.1fr)_150px_minmax(190px,1.3fr)_auto] items-start gap-3';

  return (
    <section className="flex flex-col gap-1.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold text-ink">{stateName} structure</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={resetting || saving}
            className="h-10 rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resetting ? 'Resetting…' : 'Reset to template'}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Saving…' : saved ? 'Saved' : `Save ${stateName} structure`}
          </button>
        </div>
      </div>
      <span className="text-[13px] text-ink-secondary">
        Edit the level name, posts and binding for {stateName}. Every change is stored as a {stateName}
        override and doesn’t touch the template or any other state.
      </span>
      <div className="overflow-x-auto">
        <div className="min-w-[1100px]">
          <div className={[GRID, 'border-b border-[#f3f4f6] py-2 text-[11px] uppercase tracking-[0.05em] text-ink-secondary'].join(' ')}>
            <span>Level name</span>
            <span>Official post</span>
            <span>Elected post</span>
            <span>Binding</span>
            <span>Source / members</span>
            <span>Actions</span>
          </div>
          {rows.map((r, idx) => {
            const tags = tagsFor(r);
            // Single source of truth for the Source/members cell (anchor pick / inherited mirror).
            const eff = effectiveSource(rows, idx, anchorLevels);
            return (
            <div key={r.id} className="border-b border-[#f3f4f6] py-3">
              {tags.length > 0 && (
                <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                  {tags.map((t) => (
                    <span key={t.label} className={[TAG, t.cls].join(' ')}>
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
              <div className={GRID}>
              <Input value={r.name} onChange={(e) => upd(r.id, { name: e.target.value })} placeholder="Level name" />
              <Input value={r.post} onChange={(e) => upd(r.id, { post: e.target.value })} placeholder="No official post" />
              <Input
                value={r.epost}
                onChange={(e) => upd(r.id, { epost: e.target.value })}
                placeholder="No elected post"
                style={{ borderColor: '#b9a6da' }}
              />
              <Select
                value={r.binding || undefined}
                placeholder="Select binding"
                onChange={(v) => changeBinding(r.id, v)}
                // Options + help text come from GET /platform/bindings/ (single source of truth).
                // "Inherited" is only valid when there's an Anchor level ABOVE this row to inherit
                // from — hide it otherwise (root, or rows with only Own levels above).
                options={(nearestAnchorAbove(rows, idx)
                  ? bindingOptions
                  : bindingOptions.filter((o) => o.value !== 'inherited')
                ).map((o) => ({ value: o.value as Binding, label: o.label }))}
                // Show each binding's description under its label in the dropdown.
                optionRender={(opt) => {
                  const desc = bindingDesc[String(opt.value)];
                  return (
                    <div className="flex flex-col gap-0.5 py-0.5">
                      <span className="text-sm text-ink">{opt.label}</span>
                      {desc && <span className="text-xs leading-4 text-ink-secondary">{desc}</span>}
                    </div>
                  );
                }}
                className="w-full"
              />
              {/* Source / members — anchor picks a source, inherited mirrors the anchor above, own sets members in the units editor. */}
              <div className="flex min-w-0 flex-col gap-1">
                {r.binding === 'anchor' ? (
                  eff.options.length > 0 ? (
                    <Select
                      className="w-full"
                      placeholder="Select a source level"
                      value={eff.label}
                      onChange={(v) => {
                        const a = eff.options.find((x) => x.label === v);
                        upd(r.id, { sourceLabel: v, source: a?.source ?? r.source, sourceLevel: a?.level });
                      }}
                      options={eff.options.map((a) => ({ value: a.label, label: a.label }))}
                    />
                  ) : (
                    <span className="text-[13px] text-ink-secondary">No sources imported yet.</span>
                  )
                ) : r.binding === 'inherited' ? (
                  eff.anchorName && eff.system && eff.options.length > 0 ? (
                    <>
                      <Select
                        className="w-full"
                        placeholder="Select a source node"
                        value={eff.label}
                        onChange={(v) => {
                          // Source stays the anchor's; only the mirrored child node changes.
                          const a = eff.options.find((x) => x.label === v);
                          upd(r.id, { sourceLabel: v, source: eff.system, sourceLevel: a?.level });
                        }}
                        options={eff.options.map((a) => ({ value: a.label, label: a.label }))}
                      />
                      {/* Caption below the dropdown so it stays aligned with the row's inputs. */}
                      <span className="flex items-center gap-1.5 text-[11px] text-ink-secondary">
                        Inherited from {eff.anchorName}
                        {/* Source system is locked to the anchor above. */}
                        <span className="rounded bg-brand-shade px-1.5 py-0.5 font-semibold text-brand">
                          {eff.system}
                        </span>
                      </span>
                    </>
                  ) : (
                    <span className="text-[13px] text-ink-secondary">Inherited from the anchor above</span>
                  )
                ) : (
                  <span className="text-[13px] text-ink-secondary">Members not chosen yet</span>
                )}
              </div>
              <div className="flex flex-nowrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => drop(r.id)}
                  className="h-9 shrink-0 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
                >
                  Drop
                </button>
                <button
                  type="button"
                  onClick={() => insertBelow(r.id)}
                  className="h-9 shrink-0 whitespace-nowrap rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                >
                  + Level below
                </button>
              </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function ProfileBuilderPage() {
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { code: stateCode, name: stateName } = usePlatformState();
  // The /departments/add/adhoc route is static (no :code param) → treat a missing code as adhoc.
  const resolvedCode = code ?? 'adhoc';

  const { data: apiDept, isLoading, isError } = useGetStateDepartmentQuery(
    { code: stateCode, tcode: resolvedCode },
    { skip: !stateCode || !resolvedCode },
  );
  const { data: territories = [] } = useGetTerritoriesQuery(stateCode, { skip: !stateCode });
  const { data: ladder = [] } = useGetPeopleQuery(
    { code: stateCode, template_code: resolvedCode },
    { skip: !stateCode || !resolvedCode },
  );

  // Returning from the add form carries ?tab=People&level=&designation= so we reopen the right row.
  const [searchParams] = useSearchParams();
  const initTab = (searchParams.get('tab') as Tab) || 'Territories';
  const initLevel = searchParams.get('level');
  const initDesignation = searchParams.get('designation');

  const [tab, setTab] = useState<Tab>(initTab);
  const [selLevel, setSelLevel] = useState(0);
  // The "same as an existing hierarchy" source chosen per anchor level (keyed by level name).
  const [sameAsByLevel, setSameAsByLevel] = useState<Record<string, string>>({});
  const [accept, setAccept] = useState(false);
  const [promoted, setPromoted] = useState(false);
  const [setDeptActivation] = useUpdateStateDepartmentMutation();
  // People tab: which ladder row is expanded, keyed by `${level}|${designation}`.
  const [peopleOpen, setPeopleOpen] = useState<string | null>(
    initLevel && initDesignation ? `${initLevel}|${initDesignation}` : null,
  );

  // Territories tab — Village lookup: the bare endpoint returns a sample village to resolve.
  const { data: lookup, isFetching: lookupLoading, isError: lookupErr } = useGetVillageLookupQuery(
    { code: stateCode, department: resolvedCode },
    { skip: !stateCode },
  );

  // Fall back to a local starter for the special state-only hierarchies not yet saved.
  const fallback = useMemo(() => makeFallback(resolvedCode, stateName), [resolvedCode, stateName]);
  const dept = apiDept ?? (isError || (!isLoading && !apiDept) ? fallback : undefined);

  const levels = useMemo<StateDeptLevel[]>(() => dept?.levels ?? [], [dept]);

  // Territories: validation/gaps (API) + units (with member sub-counts) for the selected own level.
  const { data: validation } = useGetValidationQuery(
    { code: stateCode, template_code: resolvedCode },
    { skip: !stateCode || !resolvedCode },
  );
  const selName = levels[selLevel]?.name ?? '';
  const selIsOwnLevel = levels[selLevel] ? isOwn(levels[selLevel].binding) : false;
  const { data: selUnitsResp } = useGetUnitsQuery(
    { code: stateCode, template_code: resolvedCode, level: selName },
    { skip: !stateCode || !resolvedCode || !selName || !selIsOwnLevel },
  );
  // Minister & committees tab: the minister(s) holding this department + committees covering it.
  const { data: deptGov } = useGetDepartmentGovernmentQuery(
    { code: stateCode, tcode: resolvedCode },
    { skip: !stateCode || !resolvedCode },
  );
  // Anchor levels available from the state's imported sources (for the anchor "same as" dropdown).
  const { data: anchorLevels = [] } = useGetAnchorLevelsQuery(stateCode, { skip: !stateCode });
  // Own-listed levels (facility/none) are government facilities — sync their units into Resources.
  const [syncResources, { isLoading: syncingResources }] = useSyncLevelResourcesMutation();

  // Per-own-level coverage / unit counts from the territories endpoint.
  const terrMap = useMemo(() => {
    const m = new Map<string, { units: number; coverage: number | null }>();
    territories
      .filter((t) => t.template_code === resolvedCode)
      .forEach((t) => m.set(t.level, { units: t.units, coverage: t.coverage }));
    return m;
  }, [territories, resolvedCode]);

  if (isLoading && !dept) {
    return <p className="text-sm text-ink-secondary">Loading…</p>;
  }

  if (!dept) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-secondary">Department not found.</p>
        <button
          type="button"
          onClick={() => navigate('/admin/departments')}
          className="inline-flex h-10 w-fit items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          Departments
        </button>
      </div>
    );
  }

  const isParty = dept.code === 'party';
  const isAdhoc = dept.code === 'adhoc' || dept.code === 'AH';
  const covOf = (l: StateDeptLevel) =>
    l.binding === 'anchor' || l.binding === 'inherited' ? 100 : terrMap.get(l.name)?.coverage ?? 0;
  const unitsOf = (l: StateDeptLevel, _i: number) =>
    // Real count from the backend (Own = units; Anchored/Inherited = source
    // nodes, e.g. Zila Parishad = all parishads). Fall back to territories units.
    l.unit_count ?? (isOwn(l.binding) ? terrMap.get(l.name)?.units ?? 0 : 0);

  const anchors = levels.filter((l) => l.binding === 'anchor').length;
  const inherited = levels.filter((l) => l.binding === 'inherited').length;
  const own = levels.filter((l) => isOwn(l.binding)).length;
  const ownCovs = levels.filter((l) => isOwn(l.binding)).map((l) => covOf(l));
  const minCov = ownCovs.length ? Math.min(...ownCovs) : 100;

  const badge = isParty || isAdhoc ? `${stateName} only` : dept.customised ? 'Customised' : 'Template as-is';
  const summary = isAdhoc
    ? `${stateName}-only · no template · ${own} of ${levels.length} own levels`
    : `${dept.control} · ${own} of ${levels.length} own levels`;

  const stats = [
    { label: 'Anchors', value: anchors, cls: 'text-brand' },
    { label: 'Inherited', value: inherited, cls: 'text-brand-dark' },
    { label: 'Own levels', value: own, cls: 'text-[#73300b]' },
    { label: 'Coverage', value: `${minCov}%`, cls: covColor(minCov) },
  ];

  const sel = levels[selLevel] ?? levels[0];
  const minister = dept.minister;
  const activated = Boolean(dept.activated);
  const activate = (on: boolean) => {
    void setDeptActivation({ code: stateCode, tcode: dept.code, patch: { activated: on } })
      .unwrap()
      .catch(() => {});
  };

  // --- Selected-level panel, village lookup and validation (Territories tab) ---
  const nextLvl = levels[selLevel + 1];
  const anchorAbove = (idx: number) => {
    for (let j = idx - 1; j >= 0; j -= 1) if (levels[j].binding === 'anchor') return j;
    return -1;
  };
  const dependents = levels
    .map((l, i) => ({ l, i }))
    .filter((x) => x.l.binding === 'inherited' && anchorAbove(x.i) === selLevel)
    .map((x) => x.l.name);
  const inheritedBelow = dependents.length
    ? dependents.join(', ')
    : nextLvl
      ? `none (${nextLvl.name} below is own)`
      : 'none (lowest level)';
  // "Units in boundary": for own levels show real units + their member sub-counts; else the node.
  const pluralize = (lvl: string, n: number) => {
    const base = lvl.toLowerCase();
    return n === 1 || base.endsWith('s') ? base : `${base}s`;
  };
  const subCountText = (u: Unit) => {
    const parts = Object.entries(u.members_by_level ?? {}).map(([lvl, n]) => `${n} ${pluralize(lvl, n)}`);
    if (parts.length) return parts.join(', ');
    return u.member_count ? `${u.member_count} members` : 'no members yet';
  };
  const selUnits: { n: string; m: string; cov: number | null }[] =
    sel && isOwn(sel.binding)
      ? (selUnitsResp?.units ?? []).map((u) => ({ n: u.name, m: subCountText(u), cov: null }))
      : sel
        ? [{ n: sel.name === 'State' ? stateName : sel.name, m: `from ${sourceText(sel)}`, cov: 100 }]
        : [];

  // Village lookup results from the API.
  const standard = lookup?.standard;
  const custom = lookup?.custom ?? [];
  // Map a department level → the matched village's value (standard path, or the own unit for Own levels).
  const resolveLevel = (l: StateDeptLevel): { text: string; sub?: string; unresolved: boolean } => {
    if (isOwn(l.binding)) {
      const hit = custom.find((c) => c.level === l.name);
      return hit ? { text: hit.unit, unresolved: false } : { text: '— not resolved yet', unresolved: true };
    }
    if (!standard) return { text: '—', unresolved: true };
    const n = l.name.toLowerCase();
    const pick = (v?: string) => (v ? { text: v, unresolved: false } : { text: '— not in sources', unresolved: true });
    if (n === 'state') return pick(standard.state);
    if (n.includes('district') || n === 'zila') return pick(standard.district);
    if (n.includes('parliament') || n.includes('lok sabha') || n === 'pc') return pick(standard.parliament);
    if (n.includes('assembly') || n.includes('vidhan') || n === 'ac') return pick(standard.assembly);
    // `standard.block` is the SEC/PS block — not the LGD Tehsil/Sub-district (no data yet), so only
    // map real block/mandal levels to it; Tehsil falls through to "not in sources".
    if (n.includes('block') || n.includes('mandal')) return pick(standard.block);
    if (n.includes('gram') || n.includes('panchayat') || n === 'gp') return pick(standard.gram_panchayat);
    if (n.includes('village') || n.includes('booth') || n.includes('ward')) return pick(standard.village);
    return { text: '— not in sources', unresolved: true };
  };

  // Validation / gaps from the API.
  const gaps = validation?.gaps ?? [];
  const info = validation?.info ?? [];
  const canActivateGaps = validation?.can_activate ?? false;

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: `Departments in ${stateName}`, to: '/admin/departments' },
          { label: dept.name },
        ]}
      />

      {/* Header */}
      <section className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div className="min-w-0 flex-1 basis-[460px]">
          <div className="text-xs font-semibold text-brand">
            {stateName.toUpperCase()} · {isParty ? 'PARTY' : isAdhoc ? 'DEPARTMENT AH' : `DEPARTMENT ${dept.code}`}
          </div>
          <h1 className="mt-1 text-2xl font-bold text-ink">{dept.name}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span
              className={[
                'rounded px-2 py-0.5 text-[11px] font-semibold',
                isParty || isAdhoc ? 'bg-brand text-white' : 'bg-brand-shade text-brand',
              ].join(' ')}
            >
              {badge}
            </span>
            <span className="text-[13px] text-ink-secondary">{summary}</span>
          </div>
        </div>
        {(isAdhoc || isParty) &&
          (promoted ? (
            <span className="inline-flex h-11 items-center rounded-lg bg-brand-shade px-4 text-[13px] font-semibold text-brand">
              Promoted to the template library
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setPromoted(true)}
              className="h-11 rounded-lg border border-brand bg-white px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
            >
              Promote to template
            </button>
          ))}
      </section>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-line/60">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={[
              '-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors',
              tab === t ? 'border-brand text-brand' : 'border-transparent text-ink-secondary hover:text-ink',
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Territories tab */}
      {tab === 'Territories' && sel && (
        <div className="flex flex-col gap-[18px]">
          <section className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 rounded-xl border border-line/30 bg-white p-5 shadow-card">
            <div className="min-w-0 flex-1 basis-[360px]">
              <h2 className="text-base font-bold text-ink">Territories and coverage</h2>
              <p className="max-w-[520px] text-[13px] text-ink-secondary">
                Units at each level, how much of the state each level covers, and whether the
                hierarchy is active.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              {stats.map((s) => (
                <div key={s.label} className="min-w-[90px] rounded-lg border border-line/50 px-3.5 py-2">
                  <div className="text-xs text-ink-secondary">{s.label}</div>
                  <div className={['text-[22px] font-bold', s.cls].join(' ')}>{s.value}</div>
                </div>
              ))}
              {(() => {
                const noGaps = validation ? gaps.length === 0 : minCov >= 100;
                const canActivate = noGaps || (accept && canActivateGaps);
                return (
                  <div className="flex flex-col items-end gap-1.5">
                    <span
                      className={[
                        'rounded px-2 py-0.5 text-[11px] font-semibold',
                        activated ? 'bg-brand-shade text-brand' : 'bg-[#fffbeb] text-[#b45309]',
                      ].join(' ')}
                    >
                      {activated ? 'Active' : noGaps ? 'Ready' : 'Draft'}
                    </span>
                    {activated ? (
                      <button
                        type="button"
                        onClick={() => activate(false)}
                        className="h-10 rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!canActivate}
                        onClick={() => activate(true)}
                        className={[
                          'h-10 rounded-lg px-4 text-[13px] font-semibold transition-colors',
                          canActivate
                            ? 'bg-brand text-white shadow-btn hover:bg-brand-dark'
                            : 'cursor-not-allowed bg-muted-bg text-ink-secondary',
                        ].join(' ')}
                      >
                        {canActivate ? 'Activate' : 'Activate (fix gaps first)'}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </section>

          <div className="flex flex-col gap-[18px] lg:flex-row">
            {/* Levels table */}
            <section className="min-w-0 flex-1 rounded-xl border border-line/30 bg-white shadow-card">
              <div className="flex flex-wrap items-baseline justify-between gap-2 p-5 pb-2">
                <h2 className="text-base font-bold text-ink">Levels, top to bottom</h2>
                <span className="text-xs text-ink-secondary">Select a level to see and edit its units</span>
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-[620px]">
                  <div className="grid grid-cols-[28px_minmax(140px,1.2fr)_130px_minmax(160px,1.4fr)_96px_120px] gap-3 border-y border-[#f3f4f6] bg-[#f9fafb] px-5 py-2 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                    <span />
                    <span>Level</span>
                    <span>Binding</span>
                    <span>Source / members</span>
                    <span>In boundary</span>
                    <span>Coverage</span>
                  </div>
                  {levels.map((l, i) => {
                    const cov = covOf(l);
                    const on = i === selLevel;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelLevel(i)}
                        className={[
                          'grid w-full grid-cols-[28px_minmax(140px,1.2fr)_130px_minmax(160px,1.4fr)_96px_120px] items-center gap-3 border-b border-[#f3f4f6] px-5 py-3 text-left transition-colors',
                          on ? 'bg-brand-shade/40 shadow-[inset_3px_0_0_#0061ff]' : 'hover:bg-brand-bg',
                        ].join(' ')}
                      >
                        <span className={['block size-3 rounded-full', BIND_CHIP[l.binding].split(' ')[0]].join(' ')} aria-hidden />
                        <span className="font-semibold text-ink">{l.name}</span>
                        <span>
                          <span className={['inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', BIND_CHIP[l.binding]].join(' ')}>
                            {l.binding_label || BIND_LABEL[l.binding]}
                          </span>
                        </span>
                        <span className="text-xs text-[#434655]">{sourceText(l)}</span>
                        <span className="text-[13px] text-ink">{unitsOf(l, i)}</span>
                        <span className="flex flex-col gap-1">
                          <span className="block h-1.5 overflow-hidden rounded-full bg-muted-bg">
                            <span className={['block h-1.5 rounded-full', barColor(cov)].join(' ')} style={{ width: `${cov}%` }} />
                          </span>
                          <span className={['text-xs', covColor(cov)].join(' ')}>{cov}%</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Right column: selected level detail + village lookup */}
            <div className="flex w-full flex-col gap-[18px] lg:w-[360px] lg:shrink-0">
              <aside className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-5 shadow-card">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                    Level {selLevel + 1} of {levels.length}
                  </div>
                  <h3 className="text-xl font-bold text-ink">{sel.name}</h3>
                  <div className="text-[13px] text-ink-secondary">
                    Post at this level:{' '}
                    <b className="text-ink">{sel.official_post || 'Set in the department’s designations'}</b>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-ink-secondary">Binding</span>
                  <span className={['inline-flex w-fit items-center rounded-full px-3 py-1.5 text-xs font-semibold', BIND_CHIP[sel.binding]].join(' ')}>
                    {sel.binding_label || BIND_LABEL[sel.binding]}
                  </span>
                </div>

                <div className="rounded-lg border border-line/50 bg-brand-bg/50 p-3 text-[13px] text-ink-secondary">
                  How this level is built is changed on the <b className="text-ink">Structure</b> tab.
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setTab('Structure')}
                      className="h-9 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                    >
                      Go to Structure
                    </button>
                  </div>
                </div>

                {sel.binding === 'anchor' && (
                  <div className="flex flex-col gap-2 rounded-lg bg-brand-shade/60 p-3">
                    <span className="text-xs font-semibold text-brand-dark">Same as this level of an existing hierarchy</span>
                    {anchorLevels.length > 0 ? (
                      <Select
                        className="w-full"
                        placeholder="Select a source level"
                        // Controlled by the selected level so it updates when a different row is
                        // clicked: the user's per-level choice, else the source matching its name.
                        value={
                          sameAsByLevel[sel.name] ??
                          anchorLevels.find((a) => a.level.toLowerCase() === sel.name.toLowerCase())?.label
                        }
                        onChange={(v) => setSameAsByLevel((m) => ({ ...m, [sel.name]: v }))}
                        options={anchorLevels.map((a) => ({ value: a.label, label: a.label }))}
                      />
                    ) : (
                      <span className="rounded-md border border-line/50 bg-white px-3 py-2 text-[13px] text-ink-secondary">
                        No sources imported for {stateName} yet — import LGD/ECI/SEC in Onboarding first.
                      </span>
                    )}
                    <span className="text-[13px] text-ink">
                      Levels inherited below: <b>{inheritedBelow}</b>
                    </span>
                    <span className="text-xs text-[#434655]">
                      Territories are references to the source, so there is nothing to enter. When the
                      source changes, inherited levels update and affected own groupings are flagged.
                    </span>
                  </div>
                )}
                {sel.binding === 'inherited' && (() => {
                  // Mirror the nearest Anchored level above: locked static-hierarchy source,
                  // choosing only among levels deeper than the deepest source used above (same
                  // logic as the Structure tab).
                  const anchorAbove = nearestAnchorAbove(levels, selLevel);
                  const srcRows = levels.map((l) => ({
                    binding: l.binding,
                    name: l.name,
                    source: l.source,
                    sourceLevel: l.source_level,
                    sourceLabel: sameAsByLevel[l.name],
                  }));
                  const { options: inheritedOpts, system: inheritedSrc } = inheritOptionsFor(
                    srcRows,
                    selLevel,
                    anchorLevels,
                  );
                  return (
                    <div className="flex flex-col gap-2 rounded-lg bg-brand-shade/60 p-3">
                      <span className="flex flex-wrap items-center gap-2 text-xs font-semibold text-brand-dark">
                        Inherited from {anchorAbove?.name ?? 'the anchor above'}
                        {inheritedSrc && (
                          <span className="rounded bg-brand-shade px-2 py-0.5 text-[11px] font-semibold text-brand">
                            {inheritedSrc}
                          </span>
                        )}
                      </span>
                      {anchorAbove && inheritedSrc && inheritedOpts.length > 0 ? (
                        <Select
                          className="w-full"
                          placeholder="Select a source node"
                          value={
                            (sameAsByLevel[sel.name] &&
                            inheritedOpts.some((a) => a.label === sameAsByLevel[sel.name])
                              ? sameAsByLevel[sel.name]
                              : undefined) ??
                            inheritedOpts.find((a) => a.level.toLowerCase() === sel.name.toLowerCase())?.label
                          }
                          onChange={(v) => setSameAsByLevel((m) => ({ ...m, [sel.name]: v }))}
                          options={inheritedOpts.map((a) => ({ value: a.label, label: a.label }))}
                        />
                      ) : (
                        <span className="rounded-md border border-line/50 bg-white px-3 py-2 text-[13px] text-ink-secondary">
                          {anchorAbove
                            ? `No child nodes under ${anchorAbove.name} in the source hierarchy.`
                            : 'No Anchored level above to inherit from.'}
                        </span>
                      )}
                      <span className="text-xs text-[#434655]">
                        Source is locked to the nearest anchor above; only its child nodes can be
                        mirrored. Change the binding on the Structure tab to redefine this level.
                      </span>
                    </div>
                  );
                })()}
                {/* The units editor is reachable for every level below the root, and also for a
                    non-state root (e.g. Panchayati Raj / ULB) whose many nodes can be mapped to the
                    level below — only the single-node "State" root has nothing to open. */}
                {(selLevel > 0 || (!isOwn(sel.binding) && sel.name !== 'State')) && (
                  <div className="flex flex-col gap-2 rounded-lg border border-line/50 bg-brand-bg/50 p-3 text-[13px] text-ink-secondary">
                    {isOwn(sel.binding)
                      ? 'Each unit of this level is made of a chosen member level. Add its units (and bulk upload) in the units editor.'
                      : selLevel === 0
                        ? 'This root has many nodes. Open the units editor to map each node to the level below.'
                        : 'View, add or edit the units at this level in the units editor.'}
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/departments/${dept.code}/units/${encodeURIComponent(sel.name)}`)}
                      className="h-9 w-fit rounded-lg bg-brand px-3 text-xs font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Edit units
                    </button>
                  </div>
                )}

                {/* Own-listed levels (binding facility/none) are government facilities — each unit is
                    a resource (e.g. PHC → Primary Health Centre). Sync them into the Resource
                    directory with their territory; category/in-charge/contact are filled there. */}
                {(sel.binding === 'facility' || sel.binding === 'none') && (
                  <div className="flex flex-col gap-2 rounded-lg border border-[#d6a27c]/60 bg-[#fbeadb]/50 p-3 text-[13px] text-[#73300b]">
                    <span>
                      <b>{sel.name}</b> is an own-listed level — each unit is a government facility. Add them
                      to the Resource directory with their territory; the rest (category, in-charge, contact)
                      can be filled in there later.
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={syncingResources}
                        onClick={async () => {
                          try {
                            const res = await syncResources({
                              code: stateCode,
                              template_code: resolvedCode,
                              level: sel.name,
                            }).unwrap();
                            message.success(
                              `${res.created} added, ${res.updated} updated — ${res.total} ${sel.name} resource${res.total === 1 ? '' : 's'} in all.`,
                            );
                          } catch (e) {
                            const detail = (e as { data?: { detail?: string } })?.data?.detail;
                            message.error(detail || 'Could not add these to resources.');
                          }
                        }}
                        className="h-9 w-fit rounded-lg bg-[#73300b] px-3 text-xs font-semibold text-white shadow-btn transition-colors hover:bg-[#5a2609] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {syncingResources ? 'Adding…' : 'Add to resources'}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/admin/resource-directory')}
                        className="h-9 w-fit rounded-lg border border-[#d6a27c] bg-white px-3 text-xs font-semibold text-[#73300b] transition-colors hover:bg-[#fbeadb]"
                      >
                        Open Resource directory
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between border-b border-[#f3f4f6] pb-1.5">
                    <span className="text-xs font-semibold text-ink-secondary">Units in boundary</span>
                    <span className="text-xs text-ink-secondary">{selUnits.length}</span>
                  </div>
                  {selUnits.map((u) => (
                    <div key={u.n} className="flex items-center justify-between gap-2 py-1.5">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-ink">{u.n}</div>
                        <div className="text-xs text-ink-secondary">{u.m}</div>
                      </div>
                      {u.cov != null && (
                        <span className={['shrink-0 text-xs font-semibold', covColor(u.cov)].join(' ')}>{u.cov}%</span>
                      )}
                    </div>
                  ))}
                  {selUnits.length === 0 && <span className="py-1.5 text-[13px] text-ink-secondary">No units yet.</span>}
                </div>
              </aside>

              {/* Village lookup — how a village resolves across this hierarchy (API) */}
              <aside className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
                <div>
                  <h3 className="text-base font-bold text-ink">Village lookup</h3>
                  <div className="text-[13px] text-ink-secondary">
                    {lookup?.village ? (
                      <>
                        <b className="text-ink">{lookup.village.name}</b> resolves to, in this hierarchy:
                      </>
                    ) : (
                      'How a village resolves across this hierarchy.'
                    )}
                  </div>
                </div>

                {lookupLoading && <span className="text-[13px] text-ink-secondary">Resolving…</span>}
                {!lookupLoading && (lookupErr || !lookup?.village) && (
                  <span className="text-[13px] text-ink-secondary">No sample village available yet.</span>
                )}

                {!lookupLoading && lookup?.village && (
                  <>
                    <div className="flex flex-col">
                      {levels.map((l, i) => {
                        const r = resolveLevel(l);
                        return (
                          <div key={i} className="grid grid-cols-[16px_minmax(90px,1fr)_minmax(0,1.2fr)] items-center gap-2.5 border-b border-[#f3f4f6] py-1.5 last:border-b-0">
                            <span className={['block size-3 rounded-full', dotClass(l.binding)].join(' ')} aria-hidden />
                            <span className="text-xs text-ink-secondary">{l.name}</span>
                            <span className={['text-[13px] font-semibold', r.unresolved ? 'text-[#b42318]' : 'text-ink'].join(' ')}>
                              {r.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-xs text-ink-secondary">
                      The same village resolves in every other hierarchy too. That is how a Tehsil chosen
                      in Revenue finds its Police Station.
                    </span>
                  </>
                )}
              </aside>
            </div>
          </div>

          {/* Validation */}
          <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-bold text-ink">Validation</h2>
              <span className="text-xs text-ink-secondary">
                {gaps.length ? `${gaps.length} gap${gaps.length > 1 ? 's' : ''} to resolve or accept` : 'No gaps. Ready to activate'}
              </span>
            </div>
            {gaps.map((g) => (
              <div key={g.level} className="flex items-baseline gap-2.5 border-t border-[#f3f4f6] py-2 text-[13px]">
                <span className="rounded bg-[#fee2e2] px-2 py-0.5 text-[11px] font-semibold text-[#dc2626]">GAP</span>
                <span className="text-ink">{g.message}</span>
              </div>
            ))}
            {info.map((line) => (
              <div key={line} className="flex items-baseline gap-2.5 border-t border-[#f3f4f6] py-2 text-[13px]">
                <span className="rounded bg-brand-shade px-2 py-0.5 text-[11px] font-semibold text-brand">INFO</span>
                <span className="text-ink-secondary">{line}</span>
              </div>
            ))}
            {gaps.length > 0 && (
              <div className="flex items-start gap-2 border-t border-[#f3f4f6] pt-2 text-[13px] text-ink">
                <Checkbox checked={accept} onChange={() => setAccept((v) => !v)} disabled={!canActivateGaps} />
                <span>
                  Activate with known gaps. Unmapped villages fall back to the nearest mapped level above
                  and stay listed here.
                  {!canActivateGaps && <span className="text-ink-secondary"> (not allowed for these gaps)</span>}
                </span>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Structure tab */}
      {tab === 'Structure' && <StructureTab key={dept.code} dept={dept} stateCode={stateCode} stateName={stateName} />}

      {/* People tab — post ladder + per-level record panel (prototype `peopleVals`). */}
      {tab === 'People' && (() => {
        const totalPosts = ladder.reduce((a, x) => a + x.units, 0);
        const filledPosts = ladder.reduce((a, x) => a + x.recorded, 0);
        const PGRID = 'grid grid-cols-[48px_minmax(140px,1fr)_minmax(220px,1.6fr)_minmax(140px,1fr)_120px] gap-2.5';
        const open = ladder.find((x) => `${x.level}|${x.designation}` === peopleOpen);
        return (
          <div className="flex flex-col gap-[18px]">
            <section className="flex flex-col gap-1.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-base font-bold text-ink">{dept.name}</h2>
                <span className="text-[13px] font-semibold text-ink-secondary">
                  {filledPosts} of {totalPosts} posts have a person on record
                </span>
              </div>
              <div className="mt-1 flex flex-col">
                <div className={[PGRID, 'border-b border-[#f3f4f6] py-1.5 text-[11px] uppercase tracking-[0.05em] text-ink-secondary'].join(' ')}>
                  <span>Rank</span>
                  <span>Level</span>
                  <span>Post</span>
                  <span className="text-right">People</span>
                  <span />
                </div>
                {ladder.length === 0 && (
                  <p className="py-3 text-[13px] text-ink-secondary">No posts set for this department yet.</p>
                )}
                {ladder.map((x, i) => {
                  const key = `${x.level}|${x.designation}`;
                  const on = peopleOpen === key;
                  const canOpen = x.units > 0;
                  const lowest = i === ladder.length - 1;
                  return (
                    <div
                      key={key}
                      className={[PGRID, 'items-center border-b border-[#f3f4f6] py-2.5', on ? 'bg-brand-shade/40' : ''].join(' ')}
                    >
                      <span className="font-bold text-brand">{x.rank || i + 1}</span>
                      <span className="flex items-center gap-2 font-bold text-ink">
                        {x.level}
                        {lowest && <span className="rounded border border-line px-1.5 text-[11px] font-semibold text-ink-secondary">lowest</span>}
                      </span>
                      <span className="flex items-center gap-2 text-sm text-ink">
                        {x.designation}
                        <span className={['rounded px-1.5 py-0.5 text-[11px] font-semibold', x.type === 'elected' ? 'bg-[#ede7f6] text-[#4a2c82]' : 'bg-brand-shade text-brand'].join(' ')}>
                          {x.type === 'elected' ? 'Elected' : 'Official'}
                        </span>
                      </span>
                      <span className={['text-right text-[13px] font-semibold', x.recorded >= x.units && x.units > 0 ? 'text-brand' : 'text-[#8a4b0f]'].join(' ')}>
                        {x.recorded} of {x.units} recorded
                      </span>
                      <span className="flex justify-end">
                        {canOpen && (
                          <button
                            type="button"
                            onClick={() => setPeopleOpen(on ? null : key)}
                            className="h-9 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade"
                          >
                            {on ? 'Hide people' : 'People'}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>

            {open && (
              <LevelPeople
                stateCode={stateCode}
                stateName={stateName}
                deptCode={dept.code}
                deptName={dept.name}
                level={open.level}
                designation={open.designation}
                type={open.type}
              />
            )}
          </div>
        );
      })()}

      {/* Minister & committees tab — department-scoped government (API) */}
      {tab === 'Minister & committees' && (() => {
        const ministers = deptGov?.ministers ?? deptGov?.positions ?? [];
        const committees = deptGov?.committees ?? [];
        const personOf = (p: { person?: string; name?: string }) => p.person || p.name || '';
        return (
          <div className="flex flex-col gap-[18px]">
            <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
              <h2 className="text-base font-bold text-ink">Minister</h2>
              <span className="text-[13px] text-ink-secondary">
                Escalation goes past the senior-most officer of this department to these ministers, in
                this order.
              </span>
              {ministers.length > 0 ? (
                <div className="flex flex-col gap-2.5">
                  {ministers.map((m, i) => {
                    const prev = ministers[i - 1];
                    const reportsTo =
                      m.reports_to || (i > 0 ? prev.title || prev.designation : '');
                    return (
                      <div
                        key={`${m.title || m.designation}-${i}`}
                        className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 rounded-lg border border-line/60 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-ink">{m.title || m.designation || '—'}</div>
                          {reportsTo && <div className="text-[13px] text-ink-secondary">Reports to {reportsTo}</div>}
                        </div>
                        <span className="text-sm font-bold text-ink">{personOf(m)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : minister ? (
                <div className="flex items-center justify-between rounded-lg border border-line/60 px-4 py-3">
                  <span className="text-sm font-bold text-ink">{minister}</span>
                </div>
              ) : (
                <span className="text-[13px] font-semibold text-[#b42318]">No minister holds this department yet.</span>
              )}
              <button
                type="button"
                onClick={() => navigate('/admin/government')}
                className="mt-1 h-10 w-fit rounded-lg border border-brand bg-white px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
              >
                Change portfolios in Government
              </button>
            </section>

            <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
              <h2 className="text-base font-bold text-ink">Legislature committees</h2>
              {committees.length === 0 ? (
                <span className="text-[13px] text-ink-secondary">No committee covers this department.</span>
              ) : (
                <div className="flex flex-col gap-3">
                  {committees.map((c, i) => (
                    <div key={`${c.name}-${i}`} className="rounded-lg border border-line/50 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-ink">{c.name || 'Committee'}</span>
                        {c.type && <span className="rounded bg-brand-shade px-2 py-0.5 text-[11px] font-semibold text-brand">{c.type}</span>}
                        {c.servicing && <span className="text-xs text-ink-secondary">· {c.servicing}</span>}
                      </div>
                      {c.members && c.members.length > 0 && (
                        <div className="mt-2 flex flex-col gap-1">
                          {c.members.map((mem, j) => (
                            <div key={j} className="flex items-center justify-between gap-2 text-[13px]">
                              <span className="font-medium text-ink">{personOf(mem) || '—'}</span>
                              {mem.role && <span className="text-xs text-ink-secondary">{mem.role}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        );
      })()}
    </div>
  );
}
